import { Component, Element, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import debounce from 'debounce';
import { CmsImage, CmsLink } from '../../types/global';

export interface CmsFlipcard {
  iconPath?: string;
  headline?: string;
  text?: string;
  showImage?: boolean;
  image?: CmsImage;
  gradient: 'gradient-light' | 'gradient-dark';
  backgroundColor: 'primary' | 'primary-100';
  link?: CmsLink;
  contentAlignment: 'top' | 'bottom';
}

interface UiCmsFlipcardBody {
  front: CmsFlipcard;
  back: CmsFlipcard;
}
@Component({
  tag: 'ui-cms-flipcard',
  styleUrl: 'ui-cms-flipcard.scss',
})
export class UiCmsFlipcard {
  @Element() el: HTMLElement;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) body: UiCmsFlipcardBody = {} as any;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop({ mutable: true }) ratio: '16/9' | '4/3';

  @State()
  private imageSize: number;

  private availableSizes = [240, 480, 640, 960, 1200, 2400];

  private resizeObserver: ResizeObserver;

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    this.addResizeObserver();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  @Listen('sd-flip-front')
  onFlipFront() {
    this.onCardFlip('front');
  }

  @Listen('sd-flip-back')
  onFlipBack() {
    this.onCardFlip('back');
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.imageSize = this.availableSizes.find((size) => size > window.innerWidth);
      }, 100)
    );
    this.resizeObserver.observe(this.el);
  }

  private onCardFlip(activeSide: 'front' | 'back') {
    const clickedSideData: CmsFlipcard = activeSide === 'front' ? this.body.back : this.body.front;
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Flipcard',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Bild',
        elementLabel: clickedSideData.text,
      },
      category: {
        area: 'Content',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);
  }

  private renderCardContent(cardSide: string) {
    return (
      <div slot={cardSide} class="flipcard__content">
        <ui-cms-headline
          inverted={
            this.body[cardSide].backgroundColor === 'primary' || this.body[cardSide].gradient === 'gradient-dark'
          }
          body={{
            headline: this.body[cardSide].headline,
            leadtext: this.body[cardSide].text,
            headingLevel: 4,
            iconPath: this.body[cardSide].iconPath,
          }}
        ></ui-cms-headline>
        {this.body[cardSide].link && (
          <ui-cms-link
            inverted={
              this.body[cardSide].backgroundColor === 'primary' || this.body[cardSide].gradient === 'gradient-dark'
            }
            body={this.body[cardSide].link}
          ></ui-cms-link>
        )}
      </div>
    );
  }

  render() {
    return (
      <Host
        class={{
          'front-col-reverse': this.body.front.contentAlignment === 'bottom',
          'back-col-reverse': this.body.back.contentAlignment === 'bottom',
        }}
      >
        <sd-flipcard
          class={{
            'aspect-landscape': this.ratio === '16/9',
          }}
          front-variant={this.body.front.showImage ? this.body.front.gradient : this.body.front.backgroundColor}
          back-variant={this.body.back.showImage ? this.body.front.gradient : this.body.back.backgroundColor}
        >
          {this.renderCardContent('front')}
          {this.renderCardContent('back')}
          {this.body.front.showImage && (
            <ui-cms-image
              class="flipcard__image"
              slot="media-front"
              objectPosition={this.body.front.image?.objectPosition}
              src={this.body.front.image?.src}
              alt={this.body.front.image?.alt}
              size={this.imageSize}
            />
          )}
          {this.body.back.showImage && (
            <ui-cms-image
              class="flipcard__image"
              slot="media-back"
              objectPosition={this.body.back.image?.objectPosition}
              src={this.body.back.image?.src}
              alt={this.body.back.image?.alt}
              size={this.imageSize}
            />
          )}
        </sd-flipcard>
      </Host>
    );
  }
}
